import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, MapPin } from 'lucide-react'
import { ConfirmDialog } from '@components/ConfirmDialog.tsx'
import { Header } from '@components/Header.tsx'
import { OnlineFieldset } from '@components/OnlineFieldset.tsx'
import { useRouter } from '@router'
import { api } from '@api'
import {
	ACCESSIBILITY,
	type Accessibility,
	ACTIVITY_TYPE,
	type Activity,
	type ActivityId,
	ActivityName,
	type ActivityType,
	AUDIENCE,
	type Audience,
	COST,
	type Cost,
	WEATHER_SUIT,
	type WeatherSuit,
} from '@hewey/shared'
import {
	ACCESSIBILITY_META,
	ACTIVITY_TYPE_META,
	AUDIENCE_META,
	COST_META,
	WEATHER_SUIT_META,
} from './activityLabels.ts'
import { ImageUploadControl } from '@components/ImageUploadControl.tsx'
import { useImageSrc } from '@hooks/useImageSrc.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { ActivityImage } from './ActivityImage.tsx'
import { ActivityMap } from './ActivityMap.tsx'
import { relativeDate } from './relativeDate.ts'
import styles from './ActivityDetail.module.css'

// Visit history shown on the detail page — anything older drops off.
// Keeps the visible list tight without losing data (the array still
// holds the full history on the server).
const VISIT_HISTORY_SHOWN = 5

// Deep-link to Google Maps — no Places API, just the public search URL. It
// searches the place name so it lands on the actual listing where one exists,
// and opens the Google Maps app on phones that have it (web otherwise).
const googleMapsUrl = (placeName: string): string =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeName)}`

type Props = {
	activityId: ActivityId
	activity: Activity | null
	loading: boolean
	onPatched: (activity: Activity) => void
	onDeleted: (id: ActivityId) => void
}

export const ActivityDetail = ({
	activityId,
	activity,
	loading,
	onPatched,
	onDeleted,
}: Props) => {
	const { navigate } = useRouter()
	const online = useOnline()
	const [editingName, setEditingName] = useState(false)
	const [nameDraft, setNameDraft] = useState(activity?.name ?? '')
	const [notesDraft, setNotesDraft] = useState(activity?.notes ?? '')
	const [noteEditing, setNoteEditing] = useState(false)
	// While the textarea is focused, a background poll bringing a remote notes
	// change must not overwrite what the user is mid-way through typing.
	const notesFocusedRef = useRef(false)
	const [typeOpen, setTypeOpen] = useState(false)
	const [costOpen, setCostOpen] = useState(false)
	const [weatherOpen, setWeatherOpen] = useState(false)
	const [audienceOpen, setAudienceOpen] = useState(false)
	const [accessibilityOpen, setAccessibilityOpen] = useState(false)
	const [visiting, setVisiting] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	// Local object-URL shown in the hero while a photo upload is in flight.
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const photoSrc = useImageSrc(
		'activities',
		activityId,
		activity?.photoUrl ?? null,
	)
	const [saveStatus, setSaveStatus] = useState<
		'idle' | 'saving' | 'saved' | 'error'
	>('idle')
	const nameInputRef = useRef<HTMLInputElement>(null)
	const saveStatusTimer = useRef<number | null>(null)

	useEffect(() => {
		if (editingName) nameInputRef.current?.focus()
	}, [editingName])

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={() => navigate('/activities')}
			aria-label="Back to activities"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	useEffect(
		() => () => {
			if (saveStatusTimer.current !== null) {
				clearTimeout(saveStatusTimer.current)
			}
		},
		[],
	)

	useEffect(() => {
		// Keep the notes textarea synced when the underlying activity updates
		// (e.g. another family member edits it and we poll). Skip while the
		// field is focused so we don't clobber the user's in-progress typing.
		if (notesFocusedRef.current) return
		setNotesDraft(activity?.notes ?? '')
	}, [activity?.notes])

	if (!activity) {
		return (
			<main className={styles.screen}>
				<Header left={backButton} />
				<p className={styles.empty}>
					{loading ? 'Loading…' : 'Activity not found.'}
				</p>
			</main>
		)
	}

	const { location } = activity

	// Venue then town. Suppressed on an exact match, for a place whose
	// friendlyName is already just the town.
	const placeLabel =
		location.kind !== 'place'
			? ''
			: location.town && location.town !== location.friendlyName
				? `${location.friendlyName} · ${location.town}`
				: location.friendlyName

	const flashStatus = (next: 'saved' | 'error') => {
		setSaveStatus(next)
		if (saveStatusTimer.current !== null) {
			clearTimeout(saveStatusTimer.current)
		}
		saveStatusTimer.current = window.setTimeout(() => {
			setSaveStatus('idle')
			saveStatusTimer.current = null
		}, 1500)
	}

	const patch = async (
		patcher: (a: Activity) => Activity,
		body: Parameters<typeof api.activity.update>[1],
	) => {
		const optimistic = patcher(activity)
		onPatched(optimistic)
		setSaveStatus('saving')
		try {
			const res = await api.activity.update(activityId, body)
			onPatched(res.activity)
			flashStatus('saved')
		} catch {
			onPatched(activity)
			flashStatus('error')
		}
	}

	const saveName = async () => {
		const trimmed = nameDraft.trim()
		setEditingName(false)
		if (trimmed.length === 0 || trimmed === activity.name) return
		const parsed = ActivityName.safeParse(trimmed)
		if (!parsed.success) return
		await patch((a) => ({ ...a, name: parsed.data }), { name: parsed.data })
	}

	const toggleType = (t: ActivityType) => {
		const has = activity.types.includes(t)
		const next = has
			? activity.types.filter((x) => x !== t)
			: [...activity.types, t]
		void patch((a) => ({ ...a, types: next }), { types: next })
	}

	const setCost = (next: Cost | null) => {
		void patch((a) => ({ ...a, cost: next }), { cost: next })
	}

	const toggleWeather = (w: WeatherSuit) => {
		const has = activity.weatherSuits.includes(w)
		const next = has
			? activity.weatherSuits.filter((x) => x !== w)
			: [...activity.weatherSuits, w]
		void patch((a) => ({ ...a, weatherSuits: next }), {
			weatherSuits: next,
		})
	}

	const toggleAudience = (a: Audience) => {
		const has = activity.audience.includes(a)
		const next = has
			? activity.audience.filter((x) => x !== a)
			: [...activity.audience, a]
		void patch((prev) => ({ ...prev, audience: next }), { audience: next })
	}

	const toggleAccessibility = (x: Accessibility) => {
		const has = activity.accessibility.includes(x)
		const next = has
			? activity.accessibility.filter((y) => y !== x)
			: [...activity.accessibility, x]
		void patch((prev) => ({ ...prev, accessibility: next }), {
			accessibility: next,
		})
	}

	const markTagged = async () => {
		await patch((a) => ({ ...a, taggingStatus: 'tagged' }), {
			taggingStatus: 'tagged',
		})
	}

	const saveNotes = async () => {
		const trimmed = notesDraft.trim()
		const next = trimmed.length === 0 ? null : trimmed
		if (next === activity.notes) return
		await patch((a) => ({ ...a, notes: next }), { notes: next })
	}

	const onDelete = async () => {
		setConfirmingDelete(false)
		try {
			await api.activity.delete(activityId)
			onDeleted(activityId)
		} catch {
			// Leave it visible on failure — the user can try again.
		}
	}

	const onVisit = async () => {
		if (visiting) return
		setVisiting(true)
		// Optimistic append; the server's authoritative timestamp comes
		// back in the response and overwrites this.
		const optimisticIso = new Date().toISOString()
		onPatched({ ...activity, visits: [...activity.visits, optimisticIso] })
		try {
			const res = await api.activity.visit(activityId)
			onPatched(res.activity)
		} catch {
			onPatched(activity)
		} finally {
			setVisiting(false)
		}
	}

	const primaryType = activity.types[0] ?? null
	const typeMeta = primaryType ? ACTIVITY_TYPE_META[primaryType] : null
	// Photo wins the hero when set (or being uploaded); otherwise a place
	// falls back to its map and a home activity to the type emoji. The Google
	// Maps link in the body keeps the map reachable when a photo covers it.
	// Decided on the stored URL, not the resolved src, which the iOS build
	// only has once the photo has loaded.
	const hasPhoto = previewUrl !== null || activity.photoUrl !== null
	const recentVisits = [...activity.visits]
		.slice(-VISIT_HISTORY_SHOWN)
		.reverse()
	const lastVisit = activity.visits[activity.visits.length - 1] ?? null
	const todayString = new Date().toDateString()
	const visitedToday = activity.visits.some(
		(iso) => new Date(iso).toDateString() === todayString,
	)

	const didThisButton = (
		<button
			type="button"
			className={`${styles.didThis} ${visitedToday ? styles.didThisDone : ''}`.trim()}
			onClick={() => void onVisit()}
			disabled={visiting || visitedToday || !online}
		>
			{visiting
				? 'Saving…'
				: visitedToday
					? '✓ Done today'
					: '✓ Did this today'}
		</button>
	)

	return (
		<main className={styles.screen}>
			<Header left={backButton} right={didThisButton} />

			<div className={styles.heroWrap}>
				{hasPhoto ? (
					<ActivityImage
						src={previewUrl ?? photoSrc}
						seed={activity.id}
						type={primaryType}
						className={styles.heroImg}
						loading="eager"
					/>
				) : location.kind === 'place' ? (
					<ActivityMap
						lat={location.lat}
						lng={location.lng}
						className={styles.heroMapInner}
					/>
				) : (
					<div className={styles.heroEmojiWrap} aria-hidden="true">
						<span className={styles.heroEmoji}>
							{typeMeta?.emoji ?? '🎨'}
						</span>
					</div>
				)}
				<ImageUploadControl
					hasImage={activity.photoUrl !== null}
					onPreview={setPreviewUrl}
					onUpload={async (body) => {
						const res = await api.activity.uploadImage(
							activityId,
							body,
						)
						onPatched(res.activity)
					}}
					onRemove={async () => {
						const res = await api.activity.removeImage(activityId)
						onPatched(res.activity)
					}}
				/>
			</div>

			<OnlineFieldset className={styles.body}>
				<div className={styles.nameRow}>
					{editingName ? (
						<input
							ref={nameInputRef}
							className={styles.nameInput}
							value={nameDraft}
							maxLength={100}
							onChange={(e) => setNameDraft(e.target.value)}
							onBlur={() => void saveName()}
							onKeyDown={(e) => {
								if (e.key === 'Enter') void saveName()
								if (e.key === 'Escape') {
									setNameDraft(activity.name)
									setEditingName(false)
								}
							}}
						/>
					) : (
						<h1
							className={styles.name}
							onClick={() => {
								if (!online) return
								setNameDraft(activity.name)
								setEditingName(true)
							}}
						>
							{activity.name}
						</h1>
					)}
					{saveStatus !== 'idle' ? (
						<span
							className={`${styles.saveStatus} ${
								saveStatus === 'saved'
									? styles.saveStatusSaved
									: saveStatus === 'error'
										? styles.saveStatusError
										: ''
							}`.trim()}
							aria-live="polite"
						>
							{saveStatus === 'saving'
								? 'Saving…'
								: saveStatus === 'saved'
									? 'Saved ✓'
									: 'Save failed'}
						</span>
					) : null}
				</div>

				<div className={styles.whereRow}>
					<p className={styles.where}>
						<span aria-hidden="true">
							{location.kind === 'home' ? '🏠' : '📍'}
						</span>{' '}
						{location.kind === 'home' ? 'Home' : placeLabel}
					</p>
				</div>
				{location.kind === 'place' ? (
					<div className={styles.mapLinks}>
						<a
							className={styles.mapLink}
							href={googleMapsUrl(location.friendlyName)}
							target="_blank"
							rel="noreferrer"
						>
							<MapPin size={15} aria-hidden="true" /> Open in
							Google Maps
						</a>
					</div>
				) : null}
				{lastVisit ? (
					<p className={styles.lastVisit}>
						Last done {relativeDate(lastVisit)}
					</p>
				) : null}

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Notes</h2>
					{notesDraft.trim().length === 0 && !noteEditing ? (
						<button
							type="button"
							className={styles.notesPrompt}
							onClick={() => setNoteEditing(true)}
						>
							+ Add a note
						</button>
					) : (
						<textarea
							className={styles.notes}
							value={notesDraft}
							autoFocus={noteEditing}
							onChange={(e) => setNotesDraft(e.target.value)}
							onFocus={() => {
								notesFocusedRef.current = true
							}}
							onBlur={() => {
								notesFocusedRef.current = false
								setNoteEditing(false)
								void saveNotes()
							}}
							placeholder="Parking tips, what to bring…"
							maxLength={2000}
							rows={4}
						/>
					)}
				</section>

				{activity.taggingStatus === 'pending' ? (
					<div className={styles.pending}>
						<p className={styles.pendingText}>
							This activity is listed under “Needs tagging” until
							you mark it done.
						</p>
						<button
							type="button"
							className={styles.pendingAction}
							onClick={() => void markTagged()}
						>
							Mark as tagged
						</button>
					</div>
				) : null}

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Type</h2>
					<div className={styles.pills}>
						{(typeOpen
							? ACTIVITY_TYPE
							: ACTIVITY_TYPE.filter((t) =>
									activity.types.includes(t),
								)
						).map((t) => {
							const meta = ACTIVITY_TYPE_META[t]
							const active = activity.types.includes(t)
							return (
								<button
									key={t}
									type="button"
									className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
									aria-pressed={active}
									onClick={() =>
										typeOpen
											? toggleType(t)
											: setTypeOpen(true)
									}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
						{typeOpen ? (
							<button
								type="button"
								className={styles.pillDone}
								onClick={() => setTypeOpen(false)}
							>
								Done
							</button>
						) : (
							<button
								type="button"
								className={styles.pillAdd}
								onClick={() => setTypeOpen(true)}
							>
								{activity.types.length > 0
									? '+ Add type'
									: '+ Set type'}
							</button>
						)}
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Cost</h2>
					<div className={styles.pills}>
						{costOpen
							? COST.map((c) => {
									const meta = COST_META[c]
									const active = activity.cost === c
									return (
										<button
											key={c}
											type="button"
											className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
											aria-pressed={active}
											onClick={() =>
												setCost(active ? null : c)
											}
										>
											<span aria-hidden="true">
												{meta.emoji}
											</span>{' '}
											{meta.label}
										</button>
									)
								})
							: activity.cost
								? (() => {
										const meta = COST_META[activity.cost]
										return (
											<button
												key={activity.cost}
												type="button"
												className={`${styles.pill} ${styles.pillActive}`}
												aria-pressed={true}
												onClick={() =>
													setCostOpen(true)
												}
											>
												<span aria-hidden="true">
													{meta.emoji}
												</span>{' '}
												{meta.label}
											</button>
										)
									})()
								: null}
						{costOpen ? (
							<button
								type="button"
								className={styles.pillDone}
								onClick={() => setCostOpen(false)}
							>
								Done
							</button>
						) : (
							<button
								type="button"
								className={styles.pillAdd}
								onClick={() => setCostOpen(true)}
							>
								{activity.cost ? '+ Change' : '+ Set cost'}
							</button>
						)}
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Weather suits</h2>
					<div className={styles.pills}>
						{(weatherOpen
							? WEATHER_SUIT
							: WEATHER_SUIT.filter((w) =>
									activity.weatherSuits.includes(w),
								)
						).map((w) => {
							const meta = WEATHER_SUIT_META[w]
							const active = activity.weatherSuits.includes(w)
							return (
								<button
									key={w}
									type="button"
									className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
									aria-pressed={active}
									onClick={() =>
										weatherOpen
											? toggleWeather(w)
											: setWeatherOpen(true)
									}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
						{weatherOpen ? (
							<button
								type="button"
								className={styles.pillDone}
								onClick={() => setWeatherOpen(false)}
							>
								Done
							</button>
						) : (
							<button
								type="button"
								className={styles.pillAdd}
								onClick={() => setWeatherOpen(true)}
							>
								+ Add tag
							</button>
						)}
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Audience</h2>
					<div className={styles.pills}>
						{(audienceOpen
							? AUDIENCE
							: AUDIENCE.filter((a) =>
									activity.audience.includes(a),
								)
						).map((a) => {
							const meta = AUDIENCE_META[a]
							const active = activity.audience.includes(a)
							return (
								<button
									key={a}
									type="button"
									className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
									aria-pressed={active}
									onClick={() =>
										audienceOpen
											? toggleAudience(a)
											: setAudienceOpen(true)
									}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
						{audienceOpen ? (
							<button
								type="button"
								className={styles.pillDone}
								onClick={() => setAudienceOpen(false)}
							>
								Done
							</button>
						) : (
							<button
								type="button"
								className={styles.pillAdd}
								onClick={() => setAudienceOpen(true)}
							>
								+ Add tag
							</button>
						)}
					</div>
				</section>

				<section className={styles.section}>
					<h2 className={styles.sectionTitle}>Accessibility</h2>
					<div className={styles.pills}>
						{(accessibilityOpen
							? ACCESSIBILITY
							: ACCESSIBILITY.filter((x) =>
									activity.accessibility.includes(x),
								)
						).map((x) => {
							const meta = ACCESSIBILITY_META[x]
							const active = activity.accessibility.includes(x)
							return (
								<button
									key={x}
									type="button"
									className={`${styles.pill} ${active ? styles.pillActive : ''}`.trim()}
									aria-pressed={active}
									onClick={() =>
										accessibilityOpen
											? toggleAccessibility(x)
											: setAccessibilityOpen(true)
									}
								>
									<span aria-hidden="true">{meta.emoji}</span>{' '}
									{meta.label}
								</button>
							)
						})}
						{accessibilityOpen ? (
							<button
								type="button"
								className={styles.pillDone}
								onClick={() => setAccessibilityOpen(false)}
							>
								Done
							</button>
						) : (
							<button
								type="button"
								className={styles.pillAdd}
								onClick={() => setAccessibilityOpen(true)}
							>
								+ Add tag
							</button>
						)}
					</div>
				</section>

				{recentVisits.length > 0 ? (
					<section className={styles.section}>
						<h2 className={styles.sectionTitle}>Recent visits</h2>
						<ul className={styles.visitList}>
							{recentVisits.map((iso, idx) => (
								<li key={`${iso}-${idx}`}>
									<span className={styles.visitDate}>
										{relativeDate(iso)}
									</span>
									<span className={styles.visitAbsolute}>
										{new Date(iso).toLocaleDateString(
											undefined,
											{
												day: 'numeric',
												month: 'short',
												year: 'numeric',
											},
										)}
									</span>
								</li>
							))}
						</ul>
					</section>
				) : null}

				<button
					type="button"
					className={styles.delete}
					onClick={() => setConfirmingDelete(true)}
				>
					Delete activity
				</button>
			</OnlineFieldset>

			<ConfirmDialog
				open={confirmingDelete}
				title="Delete activity"
				body={`This will remove "${activity.name}" for everyone in your household, along with its photo and visit history.`}
				confirmLabel="Delete"
				destructive
				onConfirm={() => void onDelete()}
				onClose={() => setConfirmingDelete(false)}
			/>
		</main>
	)
}
